import { Flex, StarHalfIcon, StarFilledIcon } from '@iheartjane/reefer';
import type { IconSize, Spacing } from '@iheartjane/reefer';

export interface RatingStarsProps {
  /** Space between stars. Defaults to 4. */
  gap?: Spacing;
  /** Rating out of 5. Values outside [0, 5] are clamped. */
  rating: number;
  /** Size of each star. Defaults to `sm`. */
  size?: IconSize;
}

const HALF_STAR_THRESHOLD = 0.5;
const TOTAL_STARS = 5;

export function RatingStars({
  gap = 4,
  rating,
  size = 'sm',
}: RatingStarsProps) {
  const clamped = Math.min(Math.max(rating, 0), TOTAL_STARS);
  const filled = Math.floor(clamped);
  const hasHalf = clamped - filled >= HALF_STAR_THRESHOLD;
  const empty = TOTAL_STARS - filled - (hasHalf ? 1 : 0);

  return (
    <Flex
      alignItems="center"
      gap={gap}
      role="img"
      aria-label={`${clamped.toFixed(1)} out of 5 stars`}
    >
      {Array.from({ length: filled }, (_, index) => (
        <StarFilledIcon
          key={`filled-${index}`}
          color="gold"
          size={size}
          data-testid="rating-star-filled"
        />
      ))}
      {hasHalf && <StarHalfIcon size={size} data-testid="rating-star-half" />}
      {Array.from({ length: empty }, (_, index) => (
        <StarFilledIcon
          key={`empty-${index}`}
          color="grays-light"
          size={size}
          data-testid="rating-star-empty"
        />
      ))}
    </Flex>
  );
}
